import { useEffect, useState } from "react";
import { Plus, Power, ShieldCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { getUsers, createUser, deactivateUser } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const ROLES = [
  { value: "admin", label: "Administrador" },
  { value: "operador", label: "Operador" },
  { value: "auditor", label: "Auditor/Veedor" },
];

const ROL_STYLE = {
  admin: "bg-sky-100 text-sky-800",
  operador: "bg-emerald-100 text-emerald-800",
  auditor: "bg-amber-100 text-amber-800",
};

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", nombre: "", password: "", rol: "operador" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => getUsers().then(setUsers).catch(() => {});
  useEffect(() => { load(); }, []);

  const onCreate = async () => {
    try {
      await createUser(form);
      toast.success("Usuario creado");
      setOpen(false);
      setForm({ email: "", nombre: "", password: "", rol: "operador" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear el usuario");
    }
  };

  const onDeactivate = async (id) => {
    try {
      await deactivateUser(id);
      toast.success("Usuario desactivado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-2xl font-bold">Usuarios</h1><p className="text-sm text-muted-foreground">Gestión de cuentas y roles del sistema.</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="gap-2" data-testid="users-new-button"><Plus className="h-4 w-4" /> Nuevo usuario</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuevo usuario</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Nombre</Label><Input value={form.nombre} onChange={(e) => set("nombre", e.target.value)} data-testid="users-form-nombre" /></div>
              <div><Label>Correo</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="users-form-email" /></div>
              <div><Label>Contraseña</Label><Input type="text" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="users-form-password" /></div>
              <div><Label>Rol</Label>
                <Select value={form.rol} onValueChange={(v) => set("rol", v)}><SelectTrigger data-testid="users-form-rol"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <DialogFooter><Button onClick={onCreate} disabled={!form.email || !form.password || !form.nombre} data-testid="users-form-submit">Crear usuario</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="mt-6">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Nombre</TableHead><TableHead>Correo</TableHead><TableHead>Rol</TableHead><TableHead>Estado</TableHead><TableHead>Creado</TableHead><TableHead className="text-right">Acciones</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`users-row-${u.id}`}>
                    <TableCell className="font-medium">{u.nombre} {u.id === me?.id && <span className="text-xs text-muted-foreground">(tú)</span>}</TableCell>
                    <TableCell className="text-sm">{u.email}</TableCell>
                    <TableCell><Badge className={ROL_STYLE[u.rol]}>{u.rol_label}</Badge></TableCell>
                    <TableCell>{u.activo ? <Badge className="bg-emerald-100 text-emerald-800">Activo</Badge> : <Badge variant="secondary">Inactivo</Badge>}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{formatDate(u.created_at)}</TableCell>
                    <TableCell className="text-right">
                      {u.activo && u.id !== me?.id && (
                        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => onDeactivate(u.id)} data-testid={`users-deactivate-${u.id}`}><Power className="h-3.5 w-3.5" /> Desactivar</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      <p className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5" /> Las contraseñas se almacenan con hash bcrypt. El acceso se controla por JWT y rol.</p>
    </div>
  );
}
